import { createApi } from '@reduxjs/toolkit/query/react';

import { createBaseQueryWithReauth } from '@/services';
import { API_SCHEDULER } from '@/utils';

import { CreateTaskDto } from '../dtos/CreateTaskDto';
import { UpdateTaskDto } from '../dtos/UpdateTaskDto';
import { mapTaskApiToTask } from '../adapters';
import { Task, TaskApi, TaskFilter, TasksApiResponse } from '../interfaces';
import { PaginatedRequest, PaginatedResponse } from '@/types';

/**
 * El back ignora los filtros vacíos, pero mandarlos ensucia la URL y rompe el
 * cacheo de RTK Query (cada string vacío distinto genera otra key).
 */
const buildTasksParams = (
  filter: TaskFilter,
  paginated: PaginatedRequest
): Record<string, string | number> => {
  const params: Record<string, string | number> = {
    limit: paginated.pageSize,
    offset: (paginated.page - 1) * paginated.pageSize,
  };

  const name = filter.name?.trim();
  if (name) params.name = name;
  if (filter.methods?.length) params.method = filter.methods.join(',');
  if (filter.statuses?.length) params.status = filter.statuses.join(',');

  return params;
};

const tasksService = createApi({
  reducerPath: 'tasksService',
  baseQuery: createBaseQueryWithReauth(API_SCHEDULER),
  tagTypes: ['Task', 'Tasks'] as const,
  endpoints: (builder) => ({
    getTasks: builder.query<
      PaginatedResponse<Task>,
      { filter: TaskFilter; paginated: PaginatedRequest }
    >({
      query: ({ filter, paginated }) => ({
        url: '/tasks',
        params: buildTasksParams(filter, paginated),
      }),
      transformResponse: (response: TasksApiResponse) => ({
        items: (response?.items ?? []).map(mapTaskApiToTask),
        count: response?.total ?? 0,
      }),
      providesTags: ['Tasks'],
    }),
    getTask: builder.query<Task, string>({
      query: (taskId) => ({
        url: `/tasks/${taskId}`,
      }),
      transformResponse: (response: TaskApi) => mapTaskApiToTask(response),
      providesTags: (_result, _error, id) => [{ type: 'Task', id }],
    }),
    createTask: builder.mutation<Task, CreateTaskDto>({
      query: (body) => ({
        url: '/tasks',
        method: 'POST',
        body,
      }),
      transformResponse: (response: TaskApi) => mapTaskApiToTask(response),
      invalidatesTags: ['Tasks'],
    }),
    updateTask: builder.mutation<Task, { id: string; data: UpdateTaskDto }>({
      query: ({ id, data }) => ({
        url: `/tasks/${id}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: TaskApi) => mapTaskApiToTask(response),
      invalidatesTags: (_result, _error, { id }) => [
        'Tasks',
        { type: 'Task', id },
      ],
    }),
    deleteTask: builder.mutation<void, string>({
      query: (id) => ({
        url: `/tasks/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => ['Tasks', { type: 'Task', id }],
    }),
  }),
});

export default tasksService;
export const {
  useGetTaskQuery,
  useGetTasksQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
} = tasksService;
